import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useColors } from "../constants/Colors";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import AuthModal from "../components/AuthModal";
import EmptyState from "../components/EmptyState";
import { trpcReact } from "../trpc_client";
import AccountPreview from "../components/AccountPreview";
import { usePostHog } from "posthog-js/react";

const ProfilePlaceholder: React.FC = () => {
  const { t } = useTranslation();
  const colors = useColors();
  const navigate = useNavigate();
  const { user } = useAuth();
  const posthog = usePostHog();
  const [showAuth, setShowAuth] = useState(false);

  // Check if user exists in database
  const { data: userExists, isLoading: checkingUser } =
    trpcReact.checkUserExists.useQuery(undefined, {
      enabled: !!user,
    });

  // Get user profile if they exist
  const { data, isLoading, error } = trpcReact.getCurrentUserProfile.useQuery(
    undefined,
    {
      enabled: !!user && userExists?.exists === true,
    },
  );

  // Redirect to signup if user is authenticated but doesn't have a profile
  useEffect(() => {
    if (user && userExists && !userExists.exists && !checkingUser) {
      posthog?.capture("onboarding_gate_redirected", {
        surface: "profile_placeholder",
        destination: "signup",
        is_authenticated: true,
      });
      navigate("/signup", { replace: true });
    }
  }, [user, userExists, checkingUser, navigate, posthog]);

  if (user) {
    // Still checking if user exists in database
    if (checkingUser) {
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center px-4 text-center w-full max-w-full overflow-hidden"
          style={{ backgroundColor: colors.containerNeutralSolidSurface }}
        >
          <p className="text-lg" style={{ color: colors.textNeutralDefault }}>
            {t("loading") || "Loading..."}
          </p>
        </div>
      );
    }

    // User exists but profile is loading
    if (userExists?.exists && isLoading) {
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center px-4 text-center w-full max-w-full overflow-hidden"
          style={{ backgroundColor: colors.containerNeutralSolidSurface }}
        >
          <p className="text-lg" style={{ color: colors.textNeutralDefault }}>
            {t("loading") || "Loading your profile..."}
          </p>
        </div>
      );
    }

    // User doesn't exist - will be redirected to signup
    if (!userExists?.exists) {
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center px-4 text-center w-full max-w-full overflow-hidden"
          style={{ backgroundColor: colors.containerNeutralSolidSurface }}
        >
          <p className="text-lg" style={{ color: colors.textNeutralDefault }}>
            {t("signup.final.creating_account") || "Setting up your profile..."}
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center px-4 text-center w-full max-w-full overflow-hidden"
          style={{ backgroundColor: colors.containerNeutralSolidSurface }}
        >
          <p
            className="text-lg mb-2"
            style={{ color: colors.textNeutralDefault }}
          >
            {error.message || "Could not load your profile."}
          </p>
          <p className="text-sm" style={{ color: colors.textNeutralMuted }}>
            {t("try_again") || "Please refresh the page or try again later."}
          </p>
        </div>
      );
    }

    if (data) {
      return (
        <AccountPreview
          profileData={data}
          profileUserId={data.user_id}
          disableMobileRedirect
          isOwnProfile
        />
      );
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center w-full max-w-full overflow-hidden"
      style={{ backgroundColor: colors.containerNeutralSolidSurface }}
    >
      <EmptyState
        type="no-profile"
        title={t("profile_tab.tabbar_title") || "Profile"}
        description="Sign in to view and manage your profile."
        action={{
          label: t("sign_in") || "Sign in",
          onClick: () => setShowAuth(true),
        }}
      />
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={() => setShowAuth(false)}
      />
    </div>
  );
};

export default ProfilePlaceholder;
